import * as React from "react";
import * as ReactDOM from "react-dom";
import * as moment from "moment";
import * as enums from "../../enumerations";
import { ICalendarGroup, ICalendar } from "../../models/get";
import { IFlyoutHomeStorage } from "../../models/storage";
import { STORAGE_KEYS } from "../../utils/constants";
import * as localStorage from "../../utils/localstorage";
import * as apiCalendarGroups from "../../api/calendarGroups";
import * as apiCalendars from "../../api/calendars";
import { Flyout } from "../flyout";
import { TextBox } from "../textBox";
import { Expander } from "../expander";
import { ListView, ListViewItem, ListViewCalendarItem } from "../listView";
import { ScrollViewer, ScrollBarVisibility } from "../scrollViewer";
import { CalendarObjectType, CalendarViewMode } from "../../enumerations";

interface IProperties
{
    /**
     * Ein Wert der angibt ob das Flyout geöffnet ist.
     */
    isOpen: boolean,

    
    currentViewMode?: enums.CalendarViewMode,

    /**
     * Wird ausgeführt, sobald auf einen Kalender geklickt wird.
     */
    onCalendarGroupItemClick?: (dataSource: ICalendarGroup) => void,

    /**
     * Wird ausgeführt, sobald auf das Item "Alle Kalender" geklickt wird.
     */
    onAllCalendarsItemClick?: () => void,

    /**
     * Wird ausgeführt, sobald auf einen Kalender geklickt wird.
     */
    onCalendarItemClick?: (dataSource: ICalendar) => void,

    /**
     * Wird ausgeführt, sobald auf den Close Button oder außerhalb des Flyouts geklickt wird.
     */
    onClose?: () => void
}

interface IState
{
    /**
     * Enthält den Text der Filter TextBox.
     */
    filterValue: string,

    /**
     * Ein Wert der angibt, ob der Kalendergruppen Expander offen ist.
     */
    isCalendarGroupsExpanded: boolean,

    /**
     * Ein Wert der angibt, ob der "Sonstiges" Expander offen ist.
     */
    isGeneralExpanded: boolean,

    /**
     * Ein Wert der angibt, ob der Kalender Expander offen ist.
     */
    isCalendarsExpanded: boolean,

    /**
     * Enthält alle gefilterten oder nicht gefilterten Kalendergruppen.
     */
    calendarGroups: ICalendarGroup[],

    /**
     * Enthält alle gefilterten oder nicht gefilterten Kalender.
     */
    calendars: ICalendar[]
}

export class HomeFlyout extends React.Component<IProperties, IState>
{
    /**
     * Enthält alle nicht gefilterten Kalendergruppen.
     */
    private _calendarGroups: ICalendarGroup[];

    /**
     * Enthält alle nicht gefilterten Kalender.
     */
    private _calendars: ICalendar[];

    /**
     * Erstellt eine neue Instanz der Klasse @see HomeFlyout.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);

        var storage = localStorage.getJSON<IFlyoutHomeStorage>(STORAGE_KEYS.flyoutHome, true);
        if (storage == null)
        {
            storage = {};
            storage.isCalendarGroupsExpanded = true;
            storage.isGeneralExpanded = true;
            storage.isCalendarsExpanded = true;
        }

        this.state = {
            filterValue: null,
            isCalendarGroupsExpanded: storage.isCalendarGroupsExpanded,
            isGeneralExpanded: storage.isGeneralExpanded,
            isCalendarsExpanded: storage.isCalendarsExpanded,
            calendarGroups: [],
            calendars: []
        };

        this.initAsync(props);
    }

    /**
     * Lädt die benötigten Kalendergruppen sowie die Kalender.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        this._calendarGroups = await apiCalendarGroups.getCalendarGroups();
        this._calendars = await apiCalendars.getCalendars();
        this.setState({
            calendarGroups: this._calendarGroups,
            calendars: this._calendars
        });
    }

    /**
     * Speichert die Info ob die Expander offen oder zu sind ins Local Storage.
     */
    private saveToLocalStorage()
    {
        const storage: IFlyoutHomeStorage = {
            isCalendarGroupsExpanded: this.state.isCalendarGroupsExpanded,
            isGeneralExpanded: this.state.isGeneralExpanded,
            isCalendarsExpanded: this.state.isCalendarsExpanded
        };

        localStorage.setJSON(STORAGE_KEYS.flyoutHome, storage, true);
    }

    /**
     * Wird ausgeführt, sobald sich der Wert der Filter TextBox ändert.
     * Hier werden die Kalendergruppen oder Kalender gefiltert.
     * @param value Der konvertiert neue Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param e Das Ereignis welches diese Funktion ausgelöst hat.
     */
    private onFilterTextChanged = (value: any[], originalValue: string[], e: Event) => 
    {
        var filteredGroups = this._calendarGroups.where(g => g.group_name.toLowerCase().indexOf(value[0] ? value[0].toLowerCase() : "") >= 0);
        var filteredCals = this._calendars.where(g => g.name.toLowerCase().indexOf(value[0] ? value[0].toLowerCase() : "") >= 0);

        this.setState({
            filterValue: value[0],
            calendarGroups: filteredGroups,
            calendars: filteredCals
        });
    }

    /**
     * Wird ausgeführt, sobald auf den Kalendergruppen Expander geklickt wird.
     */
    private onCalendarGroupExpanderClick = (): void =>
    {
        const isExpanded = this.state.isCalendarGroupsExpanded;
        this.setState({
            isCalendarGroupsExpanded: !isExpanded
        }, () => this.saveToLocalStorage());
    }

    /**
     * Wird ausgeführt, sobald auf den Kalender Expander geklickt wird.
     */
    private onGeneralExpanderClick = (): void =>
    {
        const isExpanded = this.state.isGeneralExpanded;
        this.setState({
            isGeneralExpanded: !isExpanded
        }, () => this.saveToLocalStorage());
    }

    /**
     * Wird ausgeführt, sobald auf den "Sonstiges" Expander geklickt wird.
     */
    private onCalendarsExpanderClick = (): void =>
    {
        const isExpanded = this.state.isCalendarsExpanded;
        this.setState({
            isCalendarsExpanded: !isExpanded
        }, () => this.saveToLocalStorage());
    }

    /**
     * Wird ausgeführt, sobald auf ein Kalendergruppen Item geklickt wird.
     * @param dataSource Der Kalendergruppen Datensatz der dem Item zugeordnet ist.
     */
    private onCalendarGroupItemClick = (dataSource: ICalendarGroup) =>
    {
        var { onCalendarGroupItemClick, onClose } = this.props;
        onCalendarGroupItemClick && onCalendarGroupItemClick(dataSource);
        onClose && onClose();
    }

    /**
     * Wird ausgeführt, sobald auf das Item "Alle Kalender" geklickt wird.
     */
    private onAllCalendarsItemClick = () =>
    {
        var { onAllCalendarsItemClick, onClose } = this.props;
        onAllCalendarsItemClick && onAllCalendarsItemClick();
        onClose && onClose();
    }

    /**
     * Wird ausgeführt, sobald auf ein Kalender Item geklickt wird.
     * @param dataSource Der Kalender Datensatz der dem Item zugeordnet ist.
     */
    private onCalendarItemClick = (dataSource: ICalendar) =>
    {
        var { onCalendarItemClick, onClose } = this.props;
        onCalendarItemClick && onCalendarItemClick(dataSource);
        onClose && onClose();
    }

    private getAllCalendarsLink(): uri.URI
    {
        const url: uri.URI = new URI();
        var newUrl = null;

        if (url.path().indexOf("profile") >= 0)
        {
            newUrl = url.setNewLocation({
                selectedObjectId: {
                    id: -1,
                    type: CalendarObjectType.all
                },
                date: moment(),
                viewMode: CalendarViewMode.month
            });
        }
        else
        {
            newUrl = url.setNewLocation({
                selectedObjectId: {
                    id: -1,
                    type: CalendarObjectType.all
                }
            });
        }

        return newUrl;
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const itemStyle = {
            paddingLeft: 25
        };
        const url: uri.URI = new URI();
        url.removeSearch("selected");
        
        return (
            <Flyout
                className="fo-home"
                headerText="Kalender"
                width={400}
                isOpen={this.props.isOpen}
                onClose={this.props.onClose}>
                <TextBox
                    style={{
                        marginLeft: 25,
                        marginRight: 25
                    }}
                    value={this.state.filterValue}
                    placeholder="Filter"
                    onTextChange={this.onFilterTextChanged} />
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.visible}
                    style={{
                        top: 50
                    }}>
                    <Expander
                        style={{
                            marginBottom: 20
                        }}
                        headerText="Gruppen"
                        headerMargin={{
                            left: 25,
                            right: 7
                        }}
                        isExpanded={this.state.isCalendarGroupsExpanded}
                        onClick={this.onCalendarGroupExpanderClick}>
                        <ListView dataSource={this.state.calendarGroups}>
                            {
                                (data: ICalendarGroup, index: number) =>
                                {
                                    let newUrl = null;
                                    let urlPath: string = url.path();

                                    if (urlPath.indexOf("profile") >= 0 || urlPath.indexOf("settings") >= 0)
                                    {
                                        newUrl = url.setNewLocation({
                                            selectedObjectId: {
                                                id: data.id,
                                                type: CalendarObjectType.calendarGroup
                                            },
                                            date: moment(),
                                            viewMode: CalendarViewMode.month
                                        });
                                    }
                                    else
                                    {
                                        newUrl = url.setNewLocation({
                                            selectedObjectId: {
                                                id: data.id,
                                                type: CalendarObjectType.calendarGroup
                                            }
                                        });
                                    }

                                    return (
                                        <ListViewItem
                                            key={data.id}
                                            dataSource={data}
                                            text={data.group_name}
                                            linkTo={newUrl.hrefRelative()}
                                            style={itemStyle}
                                            onClick={this.onCalendarGroupItemClick} />
                                    );
                                }
                            }
                        </ListView>
                    </Expander>
                    <Expander
                        style={{
                            marginBottom: 20
                        }}
                        headerText="Sonstiges"
                        headerMargin={{
                            left: 25,
                            right: 7
                        }}
                        isExpanded={this.state.isGeneralExpanded}
                        onClick={this.onGeneralExpanderClick}>
                        <ListView>
                            <ListViewItem
                                key={1}
                                text="Alle Kalender"
                                linkTo={this.getAllCalendarsLink().hrefRelative()}
                                style={itemStyle}
                                onClick={this.onAllCalendarsItemClick} />
                        </ListView>
                    </Expander>
                    <Expander
                        style={{
                            marginTop: 20
                        }}
                        headerText="Kalender"
                        headerMargin={{
                            left: 25,
                            right: 7
                        }}
                        isExpanded={this.state.isCalendarsExpanded}
                        onClick={this.onCalendarsExpanderClick}>
                        <ListView dataSource={this.state.calendars}>
                            {
                                (data: ICalendar, index: number) =>
                                {
                                    let newUrl = null;
                                    let urlPath: string = url.path();

                                    if (urlPath.indexOf("profile") >= 0 || urlPath.indexOf("settings") >= 0)
                                    {
                                        newUrl = url.setNewLocation({
                                            selectedObjectId: {
                                                id: data.id,
                                                type: CalendarObjectType.calendar
                                            },
                                            date: moment(),
                                            viewMode: CalendarViewMode.month
                                        });
                                    }
                                    else
                                    {
                                        if (this.props.currentViewMode === CalendarViewMode.planning)
                                        {
                                            newUrl = url.setNewLocation({
                                                selectedObjectId: {
                                                    id: data.id,
                                                    type: CalendarObjectType.calendar
                                                },
                                                viewMode: CalendarViewMode.workWeek
                                            });
                                        }
                                        else
                                        {
                                            newUrl = url.setNewLocation({
                                                selectedObjectId: {
                                                    id: data.id,
                                                    type: CalendarObjectType.calendar
                                                }
                                            });
                                        }
                                    }

                                    return (
                                        <ListViewCalendarItem
                                            key={data.id}
                                            dataSource={data}
                                            text={data.name}
                                            color={data.color}
                                            linkTo={newUrl.hrefRelative()}
                                            style={itemStyle}
                                            onClick={this.onCalendarItemClick} />
                                    );
                                }
                            }
                        </ListView>
                    </Expander>
                </ScrollViewer>
            </Flyout>
        );
    }
}